import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { RootState } from "app/store";
import { Spin } from "common/elements";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import parentI18n from "features/parent/i18n";
import { BackBtn } from "./BackBtn";
import { useQrLoginDisplay } from "./useQrLoginDisplay";

export const QrLoginScreen = () => {
  const navigate = useNavigate();
  const play = usePlaySound();
  const { width, height, scale } = useScale();
  const parent = useSelector((state: RootState) => state.user.parent);

  // ログイン済み（ゲスト含む）の端末ではQRログインを使わせない
  useEffect(() => {
    if (parent) {
      navigate("/", { replace: true });
    }
  }, [parent, navigate]);

  const { code, time, expired, error, retry } = useQrLoginDisplay({
    enabled: !parent,
    onSuccess: () => navigate("/"),
  });

  // 期限切れ後はポーリングが停止するため、ユーザー操作でのみ新しいコードを発行する
  const handleRefresh = () => {
    btnDelay(() => {
      play();
      retry();
    });
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* ヘッダー */}
      <div style={{ height: 90 * scale }} className="flex-col-el flex-center">
        <header
          style={{ transform: `scale(${scale})`, width: width / scale }}
          className="flex-row-el h-[90px] items-center px-6 py-4 border-b border-outline bg-white"
        >
          <BackBtn />
          <p className="ml-6 type-md text-textcolor-black">
            {parentI18n.t("MSG_QR_LOGIN_SCAN_TITLE")}
          </p>
        </header>
      </div>

      {/* メインコンテンツ */}
      <div
        style={{ height: height - 90 * scale }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: (height - 90 * scale) / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div className="bg-white rounded-2xl p-8 w-full max-w-[600px]">
            <div className="flex-col-el flex-center w-full gap-4">
              <p className="type-text type-sm text-gray2-100 text-center">
                {parentI18n.t("MSG_QR_LOGIN_DISPLAY_DESC")}
              </p>

              {error ? (
                <p className="type-text type-body text-danger text-center">
                  {parentI18n.t("MSG_QR_LOGIN_ISSUE_ERROR")}
                </p>
              ) : code ? (
                <>
                  <div className="relative">
                    <div
                      className={`flex-col-el flex-center bg-white box-border border-solid border-2 border-outline rounded-xl p-4 ${
                        expired ? "opacity-30" : ""
                      }`}
                    >
                      <QRCodeSVG size={280} value={`manapzl:login:${code}`} />
                    </div>
                    {expired && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={handleRefresh}
                          className="flex items-center justify-center w-[64px] h-[64px] rounded-full bg-white shadow-md border-none cursor-pointer type-inherit-font"
                        >
                          <span className="material-symbols-outlined type-hero text-blue-100">
                            refresh
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex-col-el items-center gap-1">
                    <p className="type-text type-sm text-gray2-100">
                      {parentI18n.t("MSG_QR_LOGIN_ISSUE_CODE_LABEL")}
                    </p>
                    <p className="type-text type-title text-textcolor-black tracking-widest">
                      {code}
                    </p>
                  </div>

                  {expired ? (
                    <p className="type-text type-body text-danger">
                      {parentI18n.t("MSG_QR_LOGIN_ISSUE_EXPIRED")}
                    </p>
                  ) : (
                    <p className="type-text type-body text-textcolor-black">
                      {parentI18n.t("MSG_QR_LOGIN_ISSUE_EXPIRES_IN", { time })}
                    </p>
                  )}
                </>
              ) : (
                <Spin />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
